import { Card } from '@crowdfpl/ui-web/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdfpl/ui-web/dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDuelById } from '@/api/duels';
import { trackUmamiEvent } from '@/helpers/analytics';
import {
  orderDuelOverlayLeagues,
  resolveDuelOverlayActiveLeagueId,
} from '@/helpers/league-duel-overlay-order';
import { cn, roundPercentages } from '@/helpers/utils';
import { useAuth } from '@/hooks/data/auth';
import { useMyLeagues } from '@/hooks/data/leagues';
import { LeagueDuelVoteBreakdown } from './league-duel-vote-breakdown';

const ACTIVE_OVERLAY_PREFIX = 'cfpl:leagues:active-overlay:';

export type LeagueDuelOverlayProps = {
  duelSlug: string;
  duelId: string;
  player1Name: string;
  player2Name: string;
  userPick: 'player_a' | 'player_b' | null;
};

function storageKey(userId: string): string {
  return `${ACTIVE_OVERLAY_PREFIX}${userId}`;
}

export function LeagueDuelOverlay({
  duelSlug,
  duelId,
  player1Name,
  player2Name,
  userPick,
}: LeagueDuelOverlayProps) {
  const navigate = useNavigate();
  const isAuthenticated = useAuth(s => s.isAuthenticated);
  const user = useAuth(s => s.user);

  const myLeagues = useMyLeagues();
  const filtered = useMemo(
    () => orderDuelOverlayLeagues(myLeagues.data ?? []),
    [myLeagues.data],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const [votesOpen, setVotesOpen] = useState(false);
  /** After closing the league menu, ignore synthetic/full-card clicks (common on touch after selecting an item). */
  const suppressVotesOpenUntilMsRef = useRef(0);

  useEffect(() => {
    if (!user?.id || filtered.length === 0) return;
    // Keep an in-session SWITCH LEAGUE pick; on fresh load prefer country over localStorage.
    if (activeId && filtered.some(l => l.id === activeId)) return;
    const raw = localStorage.getItem(storageKey(user.id));
    setActiveId(resolveDuelOverlayActiveLeagueId(filtered, raw));
  }, [user?.id, filtered, activeId]);

  const active = filtered.find(l => l.id === activeId) ?? filtered[0];

  useEffect(() => {
    if (!active?.id) return;
    trackUmamiEvent('league_duel_overlay_viewed', { leagueId: active.id });
  }, [active?.id]);

  const duelLeagueQuery = useQuery({
    queryKey: ['duel', duelSlug, duelId, 'league-split', active?.id],
    queryFn: () => getDuelById(duelSlug, { leagueId: active!.id }),
    enabled: Boolean(isAuthenticated && active?.id && duelSlug),
    staleTime: 15_000,
  });

  const split = duelLeagueQuery.data?.league_split;

  const voted = split?.voted ?? 0;
  const total = split?.total ?? 0;
  const p1 = split?.p1_votes ?? 0;
  const p2 = split?.p2_votes ?? 0;
  const [pct1, pct2] = voted > 0 ? roundPercentages(p1, p2) : [50, 50];

  if (!isAuthenticated || filtered.length === 0) return null;

  const participation = total > 0 ? voted / total : 0;

  const setLeague = (nextId: string): void => {
    if (!user?.id) return;
    setActiveId(nextId);
    localStorage.setItem(storageKey(user.id), nextId);
    trackUmamiEvent('league_duel_overlay_dropdown_changed', { leagueId: nextId });
  };

  const openVotes = (): void => {
    if (voted <= 0) return;
    if (Date.now() < suppressVotesOpenUntilMsRef.current) return;
    setVotesOpen(true);
    trackUmamiEvent('league_duel_votes_opened', { leagueId: active.id });
  };

  const handleLeagueMenuOpenChange = (open: boolean): void => {
    if (!open) {
      suppressVotesOpenUntilMsRef.current = Date.now() + 400;
    }
  };

  const leftPick = userPick === 'player_a';
  const rightPick = userPick === 'player_b';

  const cardInteractive = voted > 0;
  const votedTextClass =
    participation >= 0.66
      ? 'text-foreground/80'
      : participation >= 0.33
        ? 'text-muted-foreground'
        : 'text-muted-foreground/70';

  return (
    <>
      <Card
        className={cn(
          'league-duel-overlay-card',
          cardInteractive &&
            'cursor-pointer transition-colors hover:border-primary/30 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        role={cardInteractive ? 'button' : undefined}
        tabIndex={cardInteractive ? 0 : undefined}
        onClick={cardInteractive ? openVotes : undefined}
        onKeyDown={
          cardInteractive
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openVotes();
                }
              }
            : undefined
        }
        aria-label={
          cardInteractive
            ? `View ${active.name} votes — ${voted} of ${total} voted`
            : `${active.name} league insight`
        }
      >
        <div className="space-y-2 p-3">
          <div className="flex items-center gap-2">
            {/* Radix portals bubble selection events through the React tree; stop at this edge so Card onClick does not open the breakdown. */}
            <div
              className="min-w-0 flex-1 overflow-hidden"
              onClick={e => e.stopPropagation()}
              onPointerDown={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
            >
              <DropdownMenu onOpenChange={handleLeagueMenuOpenChange}>
                <DropdownMenuTrigger
                  type="button"
                  className={cn(
                    'league-duel-overlay-menu-trigger group inline-flex max-w-full min-w-0 cursor-pointer items-center gap-1',
                    'rounded-md px-1 py-1 -mx-1 text-left text-[13px] font-semibold leading-none text-foreground',
                    'transition-colors hover:bg-muted/60',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                  aria-label="League options"
                >
                  <span className="min-w-0 truncate">{active.name}</span>
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" sideOffset={4} className="min-w-[14rem] py-1">
                  <DropdownMenuItem
                    className="cursor-pointer text-xs py-1.5"
                    onSelect={e => {
                      e.stopPropagation();
                      navigate(`/leagues/${active.id}`);
                    }}
                  >
                    View league
                  </DropdownMenuItem>
                  {filtered.length > 1 ? (
                    <>
                      <DropdownMenuSeparator className="my-0.5" />
                      <DropdownMenuLabel className="py-1 text-[10px] font-normal uppercase tracking-wide text-muted-foreground">
                        Switch league
                      </DropdownMenuLabel>
                      {filtered.map(l => (
                        <DropdownMenuItem
                          key={l.id}
                          className="cursor-pointer text-xs py-1.5"
                          onSelect={e => {
                            e.stopPropagation();
                            setLeague(l.id);
                          }}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            {l.id === active.id ? (
                              <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                            ) : (
                              <span className="inline-block w-3.5 shrink-0" aria-hidden />
                            )}
                            <span className={cn('truncate', l.id === active.id && 'font-semibold')}>{l.name}</span>
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <span
              className={cn(
                'inline-flex shrink-0 items-center gap-1 text-[11px] font-normal tabular-nums',
                votedTextClass,
              )}
            >
              {voted}/{total} voted
              {cardInteractive ? <ChevronRight aria-hidden className="h-3 w-3 opacity-60" /> : null}
            </span>
          </div>

          {voted > 0 ? (
            <div
              className="vote-bar mt-1.5"
              role="img"
              aria-label={`League: ${player1Name} ${pct1}% (${p1} votes), ${player2Name} ${pct2}% (${p2} votes)`}
            >
              <div className="seg left" style={{ width: `${pct1}%` }} />
              <div className="seg right" style={{ width: `${pct2}%` }} />
              <span
                className={cn(
                  'label left',
                  pct1 < 22 && 'hide-label',
                  pct1 >= 78 && 'label--wide',
                  leftPick && 'font-bold',
                )}
                title={`${player1Name} ${pct1}% (${p1})`}
              >
                {leftPick ? <span aria-hidden className="mr-1 font-bold">✓</span> : null}
                {player1Name} {pct1}% ({p1})
              </span>
              <span
                className={cn(
                  'label right',
                  pct2 < 22 && 'hide-label',
                  pct2 >= 78 && 'label--wide',
                  rightPick && 'font-bold',
                )}
                title={`${player2Name} ${pct2}% (${p2})`}
              >
                {player2Name} {pct2}% ({p2})
                {rightPick ? <span aria-hidden className="ml-1 font-bold">✓</span> : null}
              </span>
            </div>
          ) : (
            <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">No league votes yet</p>
          )}
        </div>
      </Card>

      <LeagueDuelVoteBreakdown
        leagueId={active.id}
        leagueName={active.name}
        duelSlug={duelSlug}
        player1Name={player1Name}
        player2Name={player2Name}
        open={votesOpen}
        onOpenChange={setVotesOpen}
      />
    </>
  );
}
